import { useEffect } from "react";
import { useLocation } from "react-router-dom";

/**
 * Enforces <meta name="robots" content="noindex,nofollow"> on internal
 * routes and redirect-only routes that must never appear in search engines
 * (auth, admin, and legacy keyword redirects whose canonical target lives in
 * the sitemap).
 *
 * Belt-and-braces with robots.txt Disallow directives and _headers
 * X-Robots-Tag: robots.txt keeps well-behaved crawlers out, the HTTP header
 * catches them at the edge, and the meta tag makes any accidentally fetched
 * URL non-indexable.
 */
const NOINDEX_PREFIXES = ["/auth", "/admin"];
const NOINDEX_EXACT = ["/blog/best-hotels-paris", "/blog/best-hotels-paris/"];
const META_NAME = "robots";
const NOINDEX_VALUE = "noindex,nofollow";

function shouldNoIndex(pathname: string): boolean {
  if (NOINDEX_EXACT.includes(pathname)) return true;
  return NOINDEX_PREFIXES.some(
    (p) => pathname === p || pathname.startsWith(`${p}/`),
  );
}

export function NoIndexRoutes() {
  const { pathname } = useLocation();

  useEffect(() => {
    if (typeof document === "undefined") return;
    let meta = document.querySelector<HTMLMetaElement>(
      `meta[name="${META_NAME}"]`,
    );
    const previous = meta?.getAttribute("content") ?? null;

    if (shouldNoIndex(pathname)) {
      if (!meta) {
        meta = document.createElement("meta");
        meta.setAttribute("name", META_NAME);
        document.head.appendChild(meta);
      }
      meta.setAttribute("content", NOINDEX_VALUE);
      return () => {
        if (!meta) return;
        if (previous === null) meta.remove();
        else meta.setAttribute("content", previous);
      };
    }
  }, [pathname]);

  return null;
}